<template>
  <!--
    WHAT — Worklist detail tab "Columns". Edits the display projection (which
      columns show, their headers/type/format, source_column, default). Wraps
      the reused-v1 WorklistProjectionPanel, feeding it the worklist's steps and
      the LIVE output columns resolved from the join graph so the Src-col picker
      offers real DA output names.

    CONTRACT — reads record.column_config (cloned into local `columns`) and
      record.studio_config.steps (computed `steps`). Emits patch({ column_config })
      via a deep watcher on `columns`. Output columns come from
      GET /api/metrics/workflow-columns/{workflow_id}/ per step (best-effort; errors ignored).

    NOTES — hydrating guard: loadColumns() runs in async mounted(), and
      `hydrating` clears only on the following $nextTick, so the initial deep
      watch fire does not emit. Wraps a v1 panel (WorklistProjectionPanel) —
      pure display metadata only; grain/aggregation live in the step DAs.
      Not selfChrome — rendered inside ArtifactDetail's standard card.
  -->
  <!-- Worklist "Columns" — the display projection. Wraps the proven
       WorklistProjectionPanel; resolves live output columns from the join graph
       so the Src-col picker offers real columns. Patches column_config. -->
  <div>
    <LoadError v-if="columnsLoadError" retryable class="mb-2" message="Couldn't load this workflow's steps, so no columns are listed." @retry="loadColumns" />
    <WorklistProjectionPanel
      v-model="columns"
      :steps="steps"
      :output-columns="outputColumns"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { consumerSteps } from '@/utils/consumerSteps';
import { notifyLoadError } from '@/utils/loadError.js';
import LoadError from '@/components/shared/LoadError.vue';
import WorklistProjectionPanel from '@/components/Admin/MetricStudio/panels/WorklistProjectionPanel.vue';

export default {
  name: 'WorklistColumnsSection',
  components: { LoadError, WorklistProjectionPanel },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    return {
      columns: (this.record.column_config || []).map((c) => ({ ...c })),
      outputColumns: [],
      columnsLoadError: false,
      hydrating: true,
    };
  },
  computed: {
    steps() { return (this.record.studio_config || {}).steps || []; },
  },
  watch: {
    columns: { deep: true, handler() { this.emit(); } },
  },
  async mounted() {
    await this.loadColumns();
    this.$nextTick(() => { this.hydrating = false; });
  },
  methods: {
    async loadColumns() {
      const cols = [];
      const seen = new Set();
      let steps = [];
      try {
        steps = await consumerSteps(this.record.studio_config);
      } catch (error) {
        notifyLoadError(error, { feature: 'metric-studio-columns', endpoint: '/api/etl_module/data-workflows/' });
        this.columnsLoadError = true;
        return;
      }
      this.columnsLoadError = false;
      for (const s of steps) {
        if (!s.workflow_id) continue;
        try {
          const { data } = await axios.get(`/api/metrics/workflow-columns/${s.workflow_id}/`);
          for (const c of data.columns || []) if (!seen.has(c)) { seen.add(c); cols.push({ col: c }); }
        } catch { /* ignore */ }
      }
      this.outputColumns = cols;
    },
    emit() {
      if (this.hydrating) return;
      this.$emit('patch', { column_config: this.columns });
    },
  },
};
</script>
